import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, setToken, getToken } from './api.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [nurseProfile, setNurseProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setNurseProfile(null);
      setLoading(false);
      return null;
    }
    try {
      const data = await api.get('/api/auth/me');
      setUser(data.user);
      setNurseProfile(data.nurseProfile || null);
      return data.user;
    } catch {
      setUser(null);
      setNurseProfile(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onSignedOut = () => {
      setUser(null);
      setNurseProfile(null);
    };
    window.addEventListener('healathome:signed-out', onSignedOut);
    return () => window.removeEventListener('healathome:signed-out', onSignedOut);
  }, [refresh]);

  const login = useCallback(
    async (email, password) => {
      const data = await api.post('/api/auth/login', { email, password });
      setToken(data.token);
      setUser(data.user);
      await refresh();
      return data.user;
    },
    [refresh]
  );

  const register = useCallback(
    async (payload) => {
      const data = await api.post('/api/auth/register', payload);
      setToken(data.token);
      setUser(data.user);
      await refresh();
      return data.user;
    },
    [refresh]
  );

  /**
   * Completes a password reset. The server hands back a session with the new
   * password already in place, so the user lands signed in rather than being
   * bounced to the login form to retype what they just chose.
   */
  const resetPassword = useCallback(
    async (token, password) => {
      const data = await api.post('/api/auth/reset', { token, password });
      setToken(data.token);
      setUser(data.user);
      await refresh();
      return data.user;
    },
    [refresh]
  );

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setNurseProfile(null);
  }, []);

  const value = useMemo(
    () => ({ user, nurseProfile, loading, login, register, resetPassword, logout, refresh, setUser }),
    [user, nurseProfile, loading, login, register, resetPassword, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
